import { useState, useCallback } from "react";
import { CloudUpload } from "lucide-react";
import { twMerge } from "tailwind-merge";
import {
  FileUploaderBase,
  type FileUploaderError,
} from "@core/features/uploadFile";
import { Button } from "../Button";
import { Type } from "../Type";
import { Label } from "../Label";
import {
  dropzoneBaseStyles,
  dropzoneDragActiveStyles,
  dropzoneIconStyles,
  dropzoneHintStyles,
  dropzoneDisabledStyles,
  dropzoneErrorStyles,
} from "../Dropzone";
import type { AllowedFileTypes } from "@core/types/file.types";

export type UploadResult = {
  url: string;
  id?: number;
};

export type Props = {
  /** Current image URL */
  image?: string;
  /** Image alt text (used for img alt attribute) */
  altText?: string;
  /** Size of the circular image in pixels */
  size?: number;
  /** Handler for uploading files with progress */
  onUpload: (
    file: File,
    onProgress: (progress: number) => void
  ) => Promise<UploadResult>;
  /** Callback when image changes (new upload or cleared) */
  onChange: (result: UploadResult | null) => void;
  /** Allowed MIME types */
  allowedFileTypes?: AllowedFileTypes;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Error state */
  error?: boolean;
  /** Custom className */
  className?: string;
  /** Test ID */
  "data-testid"?: string;
};

const DEFAULT_ALLOWED_TYPES: AllowedFileTypes = {
  "image/jpeg": true,
  "image/png": true,
  "image/gif": true,
  "image/svg+xml": true,
  "image/webp": true,
};

/**
 * Circular image upload component with drag-and-drop and progress tracking.
 * Built on FileUploaderBase for drag-and-drop support.
 * Displays a circular image preview with upload/replace functionality.
 *
 * Note: Alt text input is rendered separately in the form flow by the
 * parent component (e.g., QuizFormField) for better layout control.
 *
 * @example
 * // Basic usage
 * <ImageField
 *   image={profileUrl}
 *   altText="Profile picture"
 *   onUpload={handleUpload}
 *   onChange={setProfileImage}
 *   size={120}
 * />
 */
export function ImageField({
  image,
  altText = "",
  size = 120,
  onUpload,
  onChange,
  allowedFileTypes = DEFAULT_ALLOWED_TYPES,
  disabled = false,
  error = false,
  className,
  "data-testid": testId = "image-field",
}: Props) {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const hasImage = !!image;

  // Handle file selection from FileUploaderBase
  const handleFileChange = useCallback(
    async (files: File[]) => {
      const file = files[0];
      if (!file) return;

      setUploadError(null);
      setIsUploading(true);
      setProgress(0);

      try {
        const result = await onUpload(file, (p) => {
          setProgress(p);
        });
        onChange(result);
      } catch (err: any) {
        setUploadError(err.message || "Upload failed");
      } finally {
        setIsUploading(false);
        setProgress(0);
      }
    },
    [onUpload, onChange]
  );

  // Handle errors from FileUploaderBase
  const handleError = useCallback((err: FileUploaderError) => {
    setUploadError(err.message);
  }, []);

  // Calculate ring progress for uploading state
  const circumference = 2 * Math.PI * (size / 2 - 4);
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  // Build accept string from allowedFileTypes
  const acceptString = Object.keys(allowedFileTypes).join(",");

  return (
    <div
      className={twMerge(
        "flex flex-col items-start gap-4 w-full",
        disabled && "opacity-50 pointer-events-none",
        className
      )}
      data-testid={testId}
    >
      <FileUploaderBase
        name={`${testId}-file`}
        accept={acceptString}
        disabled={disabled || isUploading}
        onChange={handleFileChange}
        onError={handleError}
        aria-label={hasImage ? "Replace image" : "Upload image"}
        data-testid={`${testId}-uploader`}
        className="w-full"
        renderDropzone={({
          isDragActive,
          disabled: isDropzoneDisabled,
          onClick,
          onDragOver,
          onDragLeave,
          onDrop,
        }) => (
          <div className="flex flex-col items-start gap-2 w-full">
            {/* Empty state - FileUploader design */}
            {!hasImage && !isUploading && (
              <div
                onClick={isDropzoneDisabled ? undefined : onClick}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                role="button"
                tabIndex={isDropzoneDisabled ? -1 : 0}
                onKeyDown={(e) => {
                  if (
                    !isDropzoneDisabled &&
                    (e.key === "Enter" || e.key === " ")
                  ) {
                    e.preventDefault();
                    onClick();
                  }
                }}
                className={twMerge(
                  dropzoneBaseStyles,
                  "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2",
                  isDragActive && dropzoneDragActiveStyles,
                  error && dropzoneErrorStyles,
                  isDropzoneDisabled && dropzoneDisabledStyles
                )}
                aria-disabled={isDropzoneDisabled}
                data-testid={`${testId}-button`}
              >
                <CloudUpload className={dropzoneIconStyles} />
                <Label className="cursor-pointer">
                  Click to upload or drag and drop
                </Label>
                <Type variant="caption" className={dropzoneHintStyles}>
                  PNG, JPG, GIF, SVG, WebP
                </Type>
              </div>
            )}

            {/* Uploading state - circular progress */}
            {isUploading && (
              <div
                className="relative rounded-full overflow-hidden flex items-center justify-center"
                style={{ width: size, height: size }}
                data-testid={`${testId}-button`}
              >
                <div className="relative flex items-center justify-center">
                  {/* Progress ring */}
                  <svg
                    className="absolute transform -rotate-90"
                    width={size}
                    height={size}
                  >
                    {/* Background circle */}
                    <circle
                      cx={size / 2}
                      cy={size / 2}
                      r={size / 2 - 4}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="4"
                      className="text-brand-200"
                    />
                    {/* Progress circle */}
                    <circle
                      cx={size / 2}
                      cy={size / 2}
                      r={size / 2 - 4}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeDasharray={circumference}
                      strokeDashoffset={strokeDashoffset}
                      className="text-brand-600 transition-all duration-200"
                    />
                  </svg>
                  <Type variant="h6" className="text-brand-600">
                    {progress}%
                  </Type>
                </div>
              </div>
            )}

            {/* Uploaded state - circular image */}
            {hasImage && !isUploading && (
              <>
                <div
                  onClick={isDropzoneDisabled ? undefined : onClick}
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop}
                  role="button"
                  tabIndex={isDropzoneDisabled ? -1 : 0}
                  onKeyDown={(e) => {
                    if (
                      !isDropzoneDisabled &&
                      (e.key === "Enter" || e.key === " ")
                    ) {
                      e.preventDefault();
                      onClick();
                    }
                  }}
                  className={twMerge(
                    "relative rounded-full overflow-hidden flex items-center justify-center cursor-pointer transition-all duration-200",
                    "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2",
                    "border-2 border-brand-200 hover:border-brand-400",
                    isDragActive && "border-brand-600 bg-brand-50",
                    error && dropzoneErrorStyles,
                    isDropzoneDisabled && "cursor-not-allowed"
                  )}
                  style={{ width: size, height: size }}
                  aria-disabled={isDropzoneDisabled}
                  data-testid={`${testId}-button`}
                >
                  <img
                    src={image}
                    alt={altText || "Uploaded image"}
                    className="w-full h-full object-cover"
                    data-testid={`${testId}-preview`}
                  />
                </div>

                {/* Replace button */}
                <Button
                  variant="secondary"
                  onClick={onClick}
                  disabled={isDropzoneDisabled}
                  data-testid={`${testId}-replace-button`}
                >
                  Replace
                </Button>
              </>
            )}
          </div>
        )}
      />

      {/* Error message */}
      {uploadError && (
        <Type
          variant="caption"
          className="text-error"
          role="alert"
          data-testid={`${testId}-error`}
        >
          {uploadError}
        </Type>
      )}
    </div>
  );
}

export default ImageField;
